import { DISH_CATEGORIES, type Dish, type DishCategory } from "../shared/types";

export const GUEST_ORDER_VERSION = 1;
export const MAX_ORDER_ITEMS = 100;

export interface GuestOrderItem {
  key: string;
  name: string;
  category: DishCategory | "custom";
  kind: "dish" | "custom";
  dishId?: number;
}

export interface GuestOrderDraft {
  version: typeof GUEST_ORDER_VERSION;
  guestName: string;
  items: GuestOrderItem[];
}

export type OrderTextResult =
  | { ok: true; display: string; key: string }
  | { ok: false; message: string };

export type AddOrderRequestResult =
  | { ok: false; message: string; items: GuestOrderItem[] }
  | { ok: true; kind: "custom" | "dish"; message: string; item: GuestOrderItem; items: GuestOrderItem[] };

const INVISIBLE_OR_CONTROL = /[\u0000-\u001F\u007F-\u009F\u200B-\u200D\u2060\uFEFF]/u;
const CATEGORY_VALUES = new Set<string>(DISH_CATEGORIES.map(({ value }) => value));

function normalizeOrderText(value: unknown, emptyMessage: string, noun: string): OrderTextResult {
  if (typeof value !== "string") return { ok: false, message: emptyMessage };
  const display = value.normalize("NFKC").trim().replace(/\s+/gu, " ");
  const length = Array.from(display).length;
  if (length === 0) return { ok: false, message: emptyMessage };
  if (length > 20) return { ok: false, message: `${noun}最多 20 个字符` };
  if (INVISIBLE_OR_CONTROL.test(display)) {
    return { ok: false, message: `${noun}包含不可见字符，请修改后再试` };
  }
  return { ok: true, display, key: display.toLocaleLowerCase("zh-CN") };
}

export function normalizeGuestName(value: unknown): OrderTextResult {
  return normalizeOrderText(value, "请填写你的名字", "名字");
}

export function normalizeOrderRequest(value: unknown): OrderTextResult {
  return normalizeOrderText(value, "请输入想吃的菜", "菜名");
}

export function orderItemFromDish(dish: Dish): GuestOrderItem {
  const normalized = normalizeOrderRequest(dish.name);
  return {
    key: normalized.ok ? normalized.key : `dish-${dish.id}`,
    name: dish.name,
    category: dish.category,
    kind: "dish",
    dishId: dish.id,
  };
}

export function toggleOrderDish(items: readonly GuestOrderItem[], dish: Dish): GuestOrderItem[] {
  const item = orderItemFromDish(dish);
  return items.some(({ key }) => key === item.key)
    ? items.filter(({ key }) => key !== item.key)
    : [...items, item];
}

export function addOrderRequest(
  items: readonly GuestOrderItem[],
  dishes: readonly Dish[],
  value: unknown,
): AddOrderRequestResult {
  const normalized = normalizeOrderRequest(value);
  if (!normalized.ok) return { ok: false, message: normalized.message, items: [...items] };

  const selected = items.find(({ key }) => key === normalized.key);
  if (selected) {
    return { ok: false, message: `${selected.name} 已经在点菜单里了`, items: [...items] };
  }

  const sharedDish = dishes.find((dish) => {
    const dishName = normalizeOrderRequest(dish.name);
    return dishName.ok && dishName.key === normalized.key;
  });
  if (sharedDish) {
    const item = orderItemFromDish(sharedDish);
    return {
      ok: true,
      kind: "dish",
      item,
      items: [...items, item],
      message: `${item.name} 已在菜谱中，已帮你选中`,
    };
  }

  const item: GuestOrderItem = {
    key: normalized.key,
    name: normalized.display,
    category: "custom",
    kind: "custom",
  };
  return {
    ok: true,
    kind: "custom",
    item,
    items: [...items, item],
    message: `${item.name} 已加入点菜单`,
  };
}

export function emptyGuestOrderDraft(): GuestOrderDraft {
  return { version: GUEST_ORDER_VERSION, guestName: "", items: [] };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function parseStoredItem(value: unknown, dishesById: ReadonlyMap<number, Dish>): GuestOrderItem | null {
  if (!isRecord(value) || (value.kind !== "dish" && value.kind !== "custom")) return null;

  if (value.kind === "dish" && typeof value.dishId === "number" && Number.isSafeInteger(value.dishId)) {
    const currentDish = dishesById.get(value.dishId);
    if (currentDish) return orderItemFromDish(currentDish);
  }

  const normalized = normalizeOrderRequest(value.name);
  if (!normalized.ok) return null;
  if (value.kind === "custom") {
    return { key: normalized.key, name: normalized.display, category: "custom", kind: "custom" };
  }
  if (typeof value.category !== "string" || !CATEGORY_VALUES.has(value.category)) return null;
  return {
    key: normalized.key,
    name: normalized.display,
    category: value.category as DishCategory,
    kind: "dish",
    ...(typeof value.dishId === "number" && Number.isSafeInteger(value.dishId) ? { dishId: value.dishId } : {}),
  };
}

export function parseGuestOrderDraft(raw: string | null, dishes: readonly Dish[]): GuestOrderDraft {
  if (!raw) return emptyGuestOrderDraft();
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value) || value.version !== GUEST_ORDER_VERSION || !Array.isArray(value.items)) {
      return emptyGuestOrderDraft();
    }

    const guest = typeof value.guestName === "string" && value.guestName.trim()
      ? normalizeGuestName(value.guestName)
      : null;
    const dishesById = new Map(dishes.map((dish) => [dish.id, dish]));
    const keys = new Set<string>();
    const items: GuestOrderItem[] = [];
    for (const candidate of value.items.slice(0, MAX_ORDER_ITEMS)) {
      const item = parseStoredItem(candidate, dishesById);
      if (!item || keys.has(item.key)) continue;
      keys.add(item.key);
      items.push(item);
    }

    return {
      version: GUEST_ORDER_VERSION,
      guestName: guest?.ok ? guest.display : "",
      items,
    };
  } catch {
    return emptyGuestOrderDraft();
  }
}

export function serializeGuestOrderDraft(draft: GuestOrderDraft): string {
  return JSON.stringify({
    version: GUEST_ORDER_VERSION,
    guestName: draft.guestName,
    items: draft.items.slice(0, MAX_ORDER_ITEMS),
  });
}
